import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import RichTextEditor from "@/components/RichTextEditor";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_OPTION = () => ({
  option_id: Math.random().toString(36).slice(2, 14),
  text: "",
  is_correct: false,
  point_value: 0,
});

export default function ItemEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = id && id !== "new";
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [metaTags, setMetaTags] = useState([]);
  const [form, setForm] = useState({
    title: "",
    item_type: "mc",
    stem: "",
    answer_options: [DEFAULT_OPTION(), DEFAULT_OPTION()],
    randomize_options: true,
    point_value: 1,
    item_id: "",
    topic_tag: [],
    scoring_source: "native",
    status: "draft",
    item_category: "assessment",
    likert_scale_min: 0,
    likert_scale_max: 5,
    likert_labels: {},
    correct_answers: 0,
    total_answers: 0,
  });

  const isExternalScore = form.item_type === "external_score";
  const isExternalItem = form.item_type === "external_item";
  const noStem = isExternalScore || isExternalItem;

  const fetchItem = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/items/${id}`, { withCredentials: true });
      setForm({
        title: data.title || "",
        item_type: data.item_type || "mc",
        stem: data.stem || "",
        answer_options: data.answer_options?.length > 0 ? data.answer_options : [DEFAULT_OPTION(), DEFAULT_OPTION()],
        randomize_options: data.randomize_options ?? true,
        point_value: data.point_value ?? 1,
        item_id: data.item_id || "",
        topic_tag: Array.isArray(data.topic_tag) ? data.topic_tag : data.topic_tag ? [data.topic_tag] : [],
        scoring_source: data.scoring_source || "native",
        status: data.status || "draft",
        item_category: data.item_category || "assessment",
        likert_scale_min: data.likert_scale_min ?? 0,
        likert_scale_max: data.likert_scale_max ?? 5,
        likert_labels: data.likert_labels || {},
        correct_answers: data.correct_answers ?? 0,
        total_answers: data.total_answers ?? 0,
      });
    } catch {
      toast.error("Failed to load item");
      navigate("/admin/items");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { if (isEdit) fetchItem(); }, [isEdit, fetchItem]);

  useEffect(() => {
    axios.get(`${API}/meta-tags`, { withCredentials: true })
      .then(({ data }) => setMetaTags(data))
      .catch(() => {});
  }, []);

  const updateOption = (idx, field, value) => {
    const opts = [...form.answer_options];
    opts[idx] = { ...opts[idx], [field]: value };
    if (field === "is_correct" && value && (form.item_type === "mc" || form.item_type === "cfi_mc")) {
      opts.forEach((o, i) => { if (i !== idx) o.is_correct = false; });
    }
    setForm({ ...form, answer_options: opts });
  };

  const addOption = () => {
    setForm({ ...form, answer_options: [...form.answer_options, DEFAULT_OPTION()] });
  };

  const removeOption = (idx) => {
    if (form.answer_options.length <= 2) return;
    setForm({ ...form, answer_options: form.answer_options.filter((_, i) => i !== idx) });
  };

  const updateLikertLabel = (key, value) => {
    setForm((p) => ({ ...p, likert_labels: { ...p.likert_labels, [key]: value } }));
  };

  const likertRange = [];
  for (let i = form.likert_scale_min; i <= form.likert_scale_max; i++) {
    likertRange.push(i);
  }

  // Auto-calc point_value: sum of answer option values for MC/SATA/CFI MC, ratio for external_score, 0 for task/likert/survey, admin-set for external_item
  const computedPointValue = isExternalScore
    ? (form.total_answers > 0 ? +(form.correct_answers / form.total_answers).toFixed(4) : 0)
    : isExternalItem
    ? form.point_value
    : (form.item_type === "task" || form.item_type === "likert" || form.item_category === "survey")
    ? 0
    : (form.item_type === "mc" || form.item_type === "sata" || form.item_type === "cfi_mc")
    ? form.answer_options.reduce((sum, o) => sum + (o.point_value || 0), 0)
    : form.point_value;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!noStem && !form.stem.trim()) {
      toast.error("Stem is required");
      return;
    }
    if (isExternalScore) {
      if (form.total_answers <= 0) {
        toast.error("Total Answers must be greater than 0");
        return;
      }
      if (form.correct_answers < 0 || form.correct_answers > form.total_answers) {
        toast.error("Correct Answers must be between 0 and Total Answers");
        return;
      }
    }
    if (form.item_type === "mc" || form.item_type === "cfi_mc") {
      const hasCorrect = form.answer_options.some((o) => o.is_correct);
      if (!hasCorrect) {
        toast.error("MC items require exactly one correct answer");
        return;
      }
    }
    if (form.item_type === "likert") {
      if (form.likert_scale_min >= form.likert_scale_max) {
        toast.error("Scale max must be greater than min");
        return;
      }
      if (form.likert_scale_max > 20) {
        toast.error("Scale max cannot exceed 20");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        point_value: computedPointValue,
        answer_options: form.item_type === "task" || form.item_type === "likert" || isExternalScore || isExternalItem ? [] : form.answer_options,
      };      if (isExternalScore) {
        payload.scoring_source = "external";
        payload.item_category = "assessment";
        payload.stem = "";
      }
      if (isExternalItem) {
        payload.item_category = "assessment";
        payload.stem = "";
      }
      if (form.item_type !== "likert") {
        delete payload.likert_scale_min;
        delete payload.likert_scale_max;
        delete payload.likert_labels;
      }
      if (!isExternalScore) {
        delete payload.correct_answers;
        delete payload.total_answers;
      }
      if (isEdit) {
        await axios.put(`${API}/items/${id}`, payload, { withCredentials: true });
        toast.success("Question updated");
      } else {
        await axios.post(`${API}/items`, payload, { withCredentials: true });
        toast.success("Question created");
        navigate("/admin/items");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="item-editor-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/admin/items")} data-testid="item-back-btn" className="text-[#A1A9B8] hover:text-white hover:bg-white/5 w-fit">
          <ArrowLeft className="h-4 w-4 mr-1.5" />Back to Question Bank
        </Button>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
          <button type="button" onClick={() => setForm((p) => ({ ...p, status: p.status === "draft" ? "published" : "draft" }))} data-testid="item-status-toggle">
            <Badge className={form.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20 cursor-pointer" : "bg-amber-500/15 text-amber-400 border-amber-500/20 cursor-pointer"}>
              {form.status}
            </Badge>
          </button>
          <Button type="button" onClick={handleSave} disabled={saving || !form.title.trim() || (!noStem && !form.stem.trim())} data-testid="item-save-btn"
            className="h-9 px-4 sm:px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" /><span className="hidden sm:inline">Saving...</span></>
            ) : (
              <><Save className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{isEdit ? "Save Changes" : "Create Question"}</span><span className="sm:hidden">{isEdit ? "Save" : "Create"}</span></>
            )}
          </Button>
        </div>
      </div>

      <h1 className="font-heading text-xl sm:text-2xl font-bold text-white break-words" data-testid="item-editor-title">
        {isEdit ? "Edit Question" : "Create Question"}
      </h1>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Item Type */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-5">
          <h2 className="font-body font-bold text-base text-white">Question Configuration</h2>

          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Question Type</Label>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: "mc", l: "Multiple Choice" },
                { v: "sata", l: "Select All That Apply" },
                { v: "task", l: "Task" },
                { v: "likert", l: "Likert" },
                { v: "cfi_mc", l: "CFI Multiple Choice" },
                { v: "external_score", l: "External Score" },
                { v: "external_item", l: "External Item" },
              ].map((t) => (
                <button key={t.v} type="button" onClick={() => {
                  const updates = { ...form, item_type: t.v };
                  if (t.v === "external_score") {
                    updates.scoring_source = "external";
                    updates.item_category = "assessment";
                  }
                  if (t.v === "external_item") {
                    updates.item_category = "assessment";
                  }
                  setForm(updates);
                }} data-testid={`item-type-${t.v}`}
                  className={`px-4 py-2.5 text-sm font-body rounded-lg border transition-colors ${form.item_type === t.v ? "border-white/20 bg-white/10 text-white" : "border-white/10 text-[#6E7787] hover:text-white hover:border-white/15"}`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>

          {/* Assessment / Survey toggle - locked to Assessment for external_score and external_item */}
          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Category</Label>
            <div className="flex gap-2">
              {[{ v: "assessment", l: "Assessment" }, { v: "survey", l: "Survey" }].map((c) => (
                <button key={c.v} type="button"
                  onClick={() => !(isExternalScore || isExternalItem) && setForm({ ...form, item_category: c.v })}
                  data-testid={`item-category-${c.v}`}
                  disabled={(isExternalScore || isExternalItem) && c.v === "survey"}
                  className={`px-4 py-2.5 text-sm font-body rounded-lg border transition-colors ${form.item_category === c.v ? "border-white/20 bg-white/10 text-white" : "border-white/10 text-[#6E7787] hover:text-white hover:border-white/15"} ${(isExternalScore || isExternalItem) && c.v === "survey" ? "opacity-30 cursor-not-allowed" : ""}`}
                >
                  {c.l}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#6E7787] font-body">
              {isExternalScore
                ? "External Score items are always Assessment category."
                : isExternalItem
                ? "External Items are always Assessment category. Response: correct or incorrect."
                : form.item_category === "survey"
                ? "Survey items are aggregated in the Survey Data analytics tab."
                : "Assessment items are scored and included in Comparative Analytics."}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Item Title <span className="text-red-400">*</span></Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="item-title-input" className="bg-[#0A0C10] border-white/10 text-white h-10" placeholder="e.g. Cardiac Assessment Q1" />
          </div>

          {/* Stem - hidden for external_score and external_item */}
          {!noStem && (
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Stem (question/task description) <span className="text-red-400">*</span></Label>
              <RichTextEditor
                value={form.stem}
                onChange={(v) => setForm({ ...form, stem: v })}
                placeholder="Enter the question or task description..."
                testId="item-stem-editor"
              />
            </div>
          )}

          {/* External Score fields */}
          {isExternalScore && (
            <div className="bg-[#0A0C10] border border-white/10 rounded-lg p-4 space-y-4" data-testid="external-score-fields">
              <p className="text-xs font-body" style={{ color: "var(--primary-color, #4A90D9)" }}>
                External scores are admin-entered aggregate scores. The learner does not interact with this item inside the platform.
              </p>
              <p className="text-xs text-[#6E7787] font-body">
                Records a correct/total ratio from an external source. Point value is auto-calculated.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">Correct Answers</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.correct_answers}
                    onChange={(e) => setForm({ ...form, correct_answers: parseFloat(e.target.value) || 0 })}
                    data-testid="ext-correct-answers"
                    className="bg-[#0A0C10] border-white/10 text-white h-10"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">Total Answers</Label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={form.total_answers}
                    onChange={(e) => setForm({ ...form, total_answers: parseFloat(e.target.value) || 0 })}
                    data-testid="ext-total-answers"
                    className="bg-[#0A0C10] border-white/10 text-white h-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-sm text-[#A1A9B8] font-body">Auto-calculated Point Value:</span>
                <span className="text-sm text-white font-body font-bold">{computedPointValue}</span>
              </div>
            </div>
          )}

          {/* External Item fields */}
          {isExternalItem && (
            <div className="bg-[#0A0C10] border border-white/10 rounded-lg p-4 space-y-4" data-testid="external-item-fields">
              <p className="text-xs text-[#6E7787] font-body">
                External Items are scored as correct or incorrect. Set the point value awarded when marked correct.
              </p>
              <div className="w-48 space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Point Value (when correct)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={form.point_value}
                  onChange={(e) => setForm({ ...form, point_value: parseFloat(e.target.value) || 0 })}
                  data-testid="ext-item-point-value"
                  className="bg-[#0A0C10] border-white/10 text-white h-10"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Point Value</Label>
              {isExternalItem ? (
                <div className="h-10 px-3 flex items-center rounded-md bg-[#0A0C10] border border-white/10" data-testid="item-points-display">
                  <span className="text-sm text-white font-body font-medium">{form.point_value}</span>
                  <span className="text-xs text-[#6E7787] font-body ml-2">(set above)</span>
                </div>
              ) : (
                <div className="h-10 px-3 flex items-center rounded-md bg-[#0A0C10] border border-white/10" data-testid="item-points-display">
                  <span className="text-sm text-white font-body font-medium">{computedPointValue}</span>
                  <span className="text-xs text-[#6E7787] font-body ml-2">(computed)</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Meta Tags</Label>
              <div className="flex flex-wrap gap-1.5 p-2 min-h-[40px] rounded-md bg-[#0A0C10] border border-white/10" data-testid="item-topic-input">
                {form.topic_tag.map((tid) => {
                  const tag = metaTags.find((t) => t.id === tid);
                  return tag ? (
                    <span key={tid} className="inline-flex items-center gap-1 bg-white/10 text-white text-xs font-body rounded-full px-2.5 py-1">
                      {tag.name}
                      <button type="button" onClick={() => setForm({ ...form, topic_tag: form.topic_tag.filter((x) => x !== tid) })} className="text-white/50 hover:text-white ml-0.5">&times;</button>
                    </span>
                  ) : null;
                })}
                <select
                  value=""
                  onChange={(e) => { if (e.target.value && !form.topic_tag.includes(e.target.value)) setForm({ ...form, topic_tag: [...form.topic_tag, e.target.value] }); e.target.value = ""; }}
                  className="bg-transparent border-none outline-none text-xs text-[#6E7787] font-body cursor-pointer flex-1 min-w-[100px]"
                >
                  <option value="">+ Add tag</option>
                  {metaTags.filter((t) => !form.topic_tag.includes(t.id)).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Item ID (optional)</Label>
              <Input value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} data-testid="item-ext-id-input" className="bg-[#0A0C10] border-white/10 text-white h-10" placeholder="Optional" />
            </div>
          </div>
        </div>

        {/* Answer Options (MC / SATA / CFI MC) */}
        {(form.item_type === "mc" || form.item_type === "sata" || form.item_type === "cfi_mc") && (
          <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-4" data-testid="answer-options-section">
            <div className="flex items-center justify-between">
              <h2 className="font-body font-bold text-base text-white">Answer Options</h2>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-[#A1A9B8] font-body cursor-pointer">
                  <Switch checked={form.randomize_options} onCheckedChange={(v) => setForm({ ...form, randomize_options: v })} data-testid="randomize-toggle" />
                  Randomize
                </label>
              </div>
            </div>
            <p className="text-xs text-[#6E7787] font-body">
              {form.item_type === "sata" ? "Select all correct answers." : "Select exactly one correct answer."}
              {form.item_type === "cfi_mc" && " CFI MC items can only be added to CFI-mode LEXPs."}
            </p>

            <div className="space-y-2">
              {form.answer_options.map((opt, idx) => (
                <div key={opt.option_id} className="flex items-center gap-3 bg-[#0A0C10] border border-white/10 rounded-lg px-3 py-2.5" data-testid={`option-row-${idx}`}>
                  <button type="button" onClick={() => updateOption(idx, "is_correct", !opt.is_correct)}
                    data-testid={`option-correct-${idx}`}
                    className={`h-5 w-5 rounded-${form.item_type === "sata" ? "md" : "full"} border-2 shrink-0 flex items-center justify-center transition-colors ${opt.is_correct ? "border-emerald-500 bg-emerald-500" : "border-white/20 hover:border-white/40"}`}
                  >
                    {opt.is_correct && <svg viewBox="0 0 12 12" className="h-3 w-3 text-white"><path d="M3 6l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                  </button>
                  <Input value={opt.text} onChange={(e) => updateOption(idx, "text", e.target.value)} placeholder={`Option ${idx + 1}`} data-testid={`option-text-${idx}`} className="bg-transparent border-0 text-white h-8 focus-visible:ring-0 flex-1 px-0" />
                  <Input type="number" value={opt.point_value} onChange={(e) => updateOption(idx, "point_value", parseFloat(e.target.value) || 0)} data-testid={`option-points-${idx}`} className="bg-transparent border-0 text-white h-8 w-16 text-center focus-visible:ring-0" min="0" step="0.5" />
                  <button type="button" onClick={() => removeOption(idx)} disabled={form.answer_options.length <= 2} className="text-[#6E7787] hover:text-red-400 disabled:opacity-30 p-1" data-testid={`option-remove-${idx}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <Button type="button" onClick={addOption} variant="ghost" size="sm" data-testid="add-option-btn" className="text-[#A1A9B8] hover:text-white hover:bg-white/5 text-sm">
              <Plus className="h-3.5 w-3.5 mr-1.5" />Add Option
            </Button>
          </div>
        )}

        {/* Likert Scale Configuration */}
        {form.item_type === "likert" && (
          <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-5" data-testid="likert-config-section">
            <h2 className="font-body font-bold text-base text-white">Likert Scale Configuration</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Scale Min</Label>
                <Input
                  type="number"
                  value={form.likert_scale_min}
                  onChange={(e) => setForm({ ...form, likert_scale_min: parseInt(e.target.value) || 0 })}
                  data-testid="likert-min-input"
                  className="bg-[#0A0C10] border-white/10 text-white h-10"
                  min="0"
                  max="19"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Scale Max</Label>
                <Input
                  type="number"
                  value={form.likert_scale_max}
                  onChange={(e) => setForm({ ...form, likert_scale_max: Math.min(parseInt(e.target.value) || 0, 20) })}
                  data-testid="likert-max-input"
                  className="bg-[#0A0C10] border-white/10 text-white h-10"
                  min="1"
                  max="20"
                />
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-[#A1A9B8] font-body text-sm">Scale Point Labels</Label>
              <p className="text-xs text-[#6E7787] font-body">Add optional labels for each point on the scale. Leave blank to show only the number.</p>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {likertRange.map((val) => (
                  <div key={val} className="flex items-center gap-3">
                    <span className="text-sm text-white font-body font-medium w-8 text-center shrink-0">{val}</span>
                    <Input
                      value={form.likert_labels[String(val)] || ""}
                      onChange={(e) => updateLikertLabel(String(val), e.target.value)}
                      placeholder={`Label for ${val}`}
                      data-testid={`likert-label-${val}`}
                      className="bg-[#0A0C10] border-white/10 text-white h-9 text-sm placeholder:text-[#6E7787]"
                    />
                  </div>
                ))}
              </div>
            </div>
            {/* Likert Preview */}
            <div className="space-y-2">
              <Label className="text-[#A1A9B8] font-body text-sm">Preview</Label>
              <div className="bg-[#0A0C10] border border-white/10 rounded-lg p-4">
                <div className="flex flex-wrap gap-2 justify-center">
                  {likertRange.map((val) => (
                    <div key={val} className="flex flex-col items-center gap-1">
                      <div className="h-9 w-9 rounded-lg border border-white/20 flex items-center justify-center text-sm text-white font-body hover:bg-white/10 cursor-pointer transition-colors">
                        {val}
                      </div>
                      {form.likert_labels[String(val)] && (
                        <span className="text-[9px] text-[#6E7787] font-body max-w-[60px] text-center truncate">{form.likert_labels[String(val)]}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Task info */}
        {form.item_type === "task" && (
          <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6" data-testid="task-info-section">
            <h2 className="font-body font-bold text-base text-white mb-2">Task Completion</h2>
            <p className="text-sm text-[#6E7787] font-body">
              Task items are binary — the learner marks them as done. If scoring source is <span className="text-white">native</span>, completion awards the point value.
              If <span className="text-white">external</span>, the score comes from AG import.
            </p>
          </div>
        )}

      </form>
    </div>
  );
}
